import { useContext, useEffect } from 'react';
import { useState } from 'react';
import { AppContext } from '../../../App.context';
import { ApiContext } from '../../../utils/networking/Api.context';
import CheckinOptions from './CheckinOptions';
import { notification } from 'antd';

export default function useCheckinConfigHook() {
  const { API } = useContext(ApiContext);
  const { appState, appActions } = useContext(AppContext);
  const { translate, getSelectedHotel } = appActions;
  const isSuperUser = appState?.email.includes('@civitfun') || appState.superUser;

  const [state, setState] = useState({
    userCanEdit: appState.role !== 'guest',
    isLoading: false,
    selectedHotel: null,
    configOptions: null,
    tempValues: null,
    bookingSearchConfig: null,
    languages: null,
    selectedLanguages: [],
    disabledSelectors: [],
    bookingSummary: null,
    selectedBookingSummary: [],
    hardCheckinState: 0,
    isHardCheckinEnabledPMS: false,
    pmsName: null,
  });

  const canDisable = {
    save_contract: 'send_contract',
    save_cardex: 'send_cardex',
    paperless_version: [
      'paperless_images_slider_timeout',
      'paperless_show_signature_documents',
      'paperless_auto_next_checkin',
    ],
  };

  useEffect(() => {
    actions.loadLanguages();
  }, []);

  useEffect(() => {
    actions.initView();
  }, [, appState.hotelList]);

  useEffect(() => {
    actions.setSelectedHotel(getSelectedHotel());
  }, [appState.selectedHotel]);

  useEffect(() => {
    if (appState.superUser) {
      actions.getHardCheckinValues();
    }
  }, [appState.selectedHotel]);

  const actions = {
    initView: () => {
      const searchConfigOptions = actions.getSearchConfigOptions();
      updateState({ searchConfigOptions });
    },

    getCurrentHotelPMS: hotelId => {
      updateState({ isLoading: true });
      API.pmsConfigDynamicFormByHotelIdRequest({
        hotelId: hotelId || state.selectedHotel?.key,
      })
        .then(response => {
          updateState({ pmsName: response.name, isLoading: false });
        })
        .catch(error => {
          console.error('Error cargando PMS:', error);
          updateState({ pmsName: null, isLoading: false });
        });
    },

    loadCheckinConfig: hotelId => {
      if (!hotelId) return;
      updateState({ isLoading: true });
      API.checkinConfigRequest(hotelId)
        .then(res => {
          let disabledSelectors = [];
          let response = {};

          if (res?.bookingSummary && res?.selectedBookingSummary) {
            response = {
              bookingSummary: res?.bookingSummary,
              selectedBookingSummary: res?.selectedBookingSummary,
            };
            delete res?.bookingSummary;
            delete res?.selectedBookingSummary;
          }

          if (!isSuperUser) {
            delete res?.paperless_version;
          }

          delete res?.paperless_images_slider_timeout;
          delete res?.paperless_show_signature_documents;
          delete res?.paperless_auto_next_checkin;

          Object.entries(res).forEach(([key, value]) => {
            disabledSelectors = getDisabledSelectors({
              canDisable,
              key,
              value,
              disabledSelectors,
            });
          });

          updateState({
            ...response,
            isLoading: false,
            configOptions: res,
            tempValues: res,
            disabledSelectors,
          });
        })
        .catch(err => {
          updateState({ isLoading: false });
          notification.error({
            message: err?.response?.data?.message,
          });
        });
    },

    loadBookingSearchConfig: hotelId => {
      if (!hotelId) return;
      updateState({ isLoading: true, bookingSearchConfig: null });
      API.bookingSearchConfigRequest(hotelId)
        .then(res => {
          updateState({
            isLoading: false,
            bookingSearchConfig: res,
          });
        })
        .catch(err => {
          updateState({ isLoading: false });
          notification.error({
            message: err?.response?.data?.message,
          });
        });
    },

    setSelectedHotel: hotel => {
      if (!hotel) return;
      updateState({ selectedHotel: hotel });
      if (hotel && hotel?.key != 'null') {
        actions.loadCheckinConfig(hotel.key);
        actions.loadBookingSearchConfig(hotel.key);
        actions.loadAppearence(hotel.key);
        actions.getCurrentHotelPMS(hotel.key);
      }
    },

    setValue: (key, value) => {
      if (key === 'hardCheckin') {
        updateState({ hardCheckinState: value });
        return;
      }

      const tempValues = {
        ...state.tempValues,
        [key]: value,
      };

      let disabledSelectors = state.disabledSelectors;
      disabledSelectors = getDisabledSelectors({
        canDisable,
        key,
        value,
        disabledSelectors,
      });

      updateState({ tempValues, disabledSelectors });
    },

    setBookingSearchValue: (key, value) => {
      const bookingSearch = JSON.parse(JSON.stringify(state.bookingSearchConfig));
      bookingSearch.forEach(element => {
        if (element.name == key) {
          element.state = value;
        }
      });

      updateState({ bookingSearchConfig: bookingSearch });
    },

    onSaveClicked: applyToAllHotels => {
      updateState({ isLoading: true });
      actions.updateHardCheckin(applyToAllHotels);
      API.updateCheckinConfigRequest({
        hotelId: !applyToAllHotels ? state.selectedHotel?.key : null,
        values: state.tempValues,
      })
        .then(_ => {
          notification.success({
            message: translate('checkinConfig.success'),
          });
          updateState({ isLoading: false });
        })
        .catch(err => {
          notification.error({
            message: err?.response?.data?.message,
          });
          updateState({ isLoading: false });
        });
    },

    onSaveBookingSummary: applyToAllHotels => {
      updateState({ isLoading: true });

      let values = {};
      state.bookingSummary.forEach(item => {
        values[item.key] = state.selectedBookingSummary.includes(item.id);
      });

      API.updateCheckinConfigRequest({
        hotelId: !applyToAllHotels ? state.selectedHotel?.key : null,
        values,
      })
        .then(_ => {
          notification.success({
            message: translate('checkinConfig.success'),
          });
          updateState({ isLoading: false });
        })
        .catch(err => {
          notification.error({
            message: err?.response?.data?.message,
          });
          updateState({ isLoading: false });
        });
    },

    onSaveBookingClicked: applyToAllHotels => {
      const config = state.bookingSearchConfig;

      // Primary fields that need atleas 1 to b enabled IF adding more should change traduction of allOptionsDisabled
      const fieldsToCheck = ['bookingCode', 'bookingHolder'];

      const allDisabled = fieldsToCheck.every(fieldName => {
        const field = config.find(item => item.name === fieldName);
        return field?.state === 'disabled';
      });

      if (allDisabled) {
        notification.warning({
          message: translate('checkinConfig.allOptionsDisabled'),
        });
        return;
      }

      updateState({ isLoading: true });

      API.updateBookingSearchConfigRequest({
        hotelId: !applyToAllHotels ? state.selectedHotel?.key : null,
        values: config,
      })
        .then(_ => {
          notification.success({
            message: translate('checkinConfig.success'),
          });
          updateState({ isLoading: false });
        })
        .catch(err => {
          notification.error({
            message: err?.response?.data?.message,
          });
          updateState({ isLoading: false });
        });
    },

    getSelectorOptions: configOption => {
      const {
        configProperties,
        selectorOptions,
        guestLinkSelectorOptions,
        paperlessVersionOptions,
        hardCheckinOptions,
      } = CheckinOptions;
      let options = [];

      switch (String(configOption)) {
        case configProperties.allowedSince:
        case configProperties.allowedUntil:
        case configProperties.paperlessTimeout:
        case configProperties.paperlessFinalizeTimeout:
        case configProperties.paperlessImagesSliderTimeout:
          return null;
        case configProperties.captureDocs:
          return [
            {
              key: selectorOptions.capture_docs_optional,
              literal: translate(`checkinConfig.options.capture_docs_optional`),
            },
            {
              key: selectorOptions.capture_docs_disabled,
              literal: translate(`checkinConfig.options.capture_docs_disabled`),
            },
            {
              key: selectorOptions.capture_docs_mandatory,
              literal: translate(`checkinConfig.options.capture_docs_mandatory`),
            },
          ];

        case configProperties.requiredDocuments:
          options = [
            selectorOptions.none,
            selectorOptions.one,
            selectorOptions.numAdults,
            selectorOptions.numGuests,
            selectorOptions.guestOver2,
            selectorOptions.guestOver10,
            selectorOptions.guestOver12,
            selectorOptions.guestOver14,
            selectorOptions.guestOver16,
          ];
          break;

        case configProperties.requiredSignatures:
        case configProperties.requiredCardexSignatures:
          options = [
            selectorOptions.none,
            selectorOptions.one,
            selectorOptions.oneInLastProcess,
            selectorOptions.holder,
            selectorOptions.numAdults,
            selectorOptions.guestOver14,
            selectorOptions.guestOver16,
          ];
          break;

        case configProperties.saveContract:
        case configProperties.saveCardex:
        case configProperties.paperlessShowSignatureDocuments:
        case configProperties.paperlessAutoNextCheckin:
        case configProperties.showMultibookingHolders:
        case configProperties.showBookingGuests:
        case configProperties.sendIdentityDocument:
        case configProperties.sendSignature:
        case configProperties.sendPropertyContract:
        case configProperties.sendRegistrationCard:
          options = [guestLinkSelectorOptions.yes, guestLinkSelectorOptions.no];
          break;
        case configProperties.sendCardex:
        case configProperties.sendContract:
          options = [
            guestLinkSelectorOptions.none,
            guestLinkSelectorOptions.holder,
            guestLinkSelectorOptions.numAdults,
            guestLinkSelectorOptions.numGuests,
          ];
          break;

        case configProperties.paperlessVersion:
          options = [paperlessVersionOptions.v1, paperlessVersionOptions.v2];
          break;

        case configProperties.hardCheckin:
          return [
            {
              key: hardCheckinOptions.hardCheckinAll,
              literal: translate(`checkinConfig.hardCheckinAll`),
            },
            {
              key: hardCheckinOptions.hardCheckinPaperless,
              literal: translate(`checkinConfig.hardCheckinPaperless`),
            },
            {
              key: hardCheckinOptions.hardCheckinDisabled,
              literal: translate(`checkinConfig.hardCheckinDisabled`),
            },
          ];
      }

      return options.map(option => {
        const isCheckinHolder =
          String(configOption) == configProperties.requiredDocuments && option == '1';
        const item = {
          key: option,
          literal: isCheckinHolder
            ? translate(`checkinConfig.options.o_holder`)
            : translate(`checkinConfig.options.o_${option}`),
        };

        return item;
      });
    },

    getSearchConfigOptions: () => {
      const { selectorOptions } = CheckinOptions;
      return [
        {
          key: selectorOptions.active,
          literal: translate(`checkinConfig.options.o_${selectorOptions.active}`),
        },
        {
          key: selectorOptions.disabled,
          literal: translate(`checkinConfig.options.o_${selectorOptions.disabled}`),
        },
      ];
    },

    loadLanguages: () => {
      updateState({ isLoading: true });
      API.languagesRequest()
        .then(res => {
          updateState({ isLoading: false, languages: res });
        })
        .catch(err => {
          console.warn(err);
          updateState({ isLoading: false });
          notification.error({
            message: err?.response?.data?.message,
          });
        });
    },

    loadAppearence: hotelId => {
      if (!hotelId) return;
      updateState({ isLoading: true });
      API.appearenceRequest(hotelId)
        .then(res => {
          const selectedLanguages = res.languages.map(language => {
            return language.id;
          });

          updateState({
            selectedLanguages,
          });
        })
        .catch(err => {
          console.warn(err);
          updateState({ isLoading: false });
          notification.error({
            message: err?.response?.data?.message,
          });
        });
    },

    updateLanguages: applyToAllHotels => {
      updateState({ isLoading: true });
      API.updateAppearenceRequest({
        hotelId: !applyToAllHotels ? state?.selectedHotel?.key : null,
        languages: state.selectedLanguages,
      })
        .then(_ => {
          notification.success({
            message: translate('appearence.languageSuccess'),
          });
          updateState({ isLoading: false });
        })
        .catch(error => {
          notification.error({
            message: error?.response?.data?.message,
          });
          updateState({ isLoading: false });
        });
    },

    onCheckboxPress: ({ selectedCheckBox, id, type, isSelected }) => {
      const updateCheckBox = (type, checkedList) => {
        switch (type) {
          case 'bookingSummary':
            updateState({ selectedBookingSummary: checkedList });
            break;
          case 'languages':
            updateState({ selectedLanguages: checkedList });
            break;
          default:
            notification.error({
              message: 'Does not have a type',
            });
        }
      };

      if (isSelected) {
        const checkedList = selectedCheckBox;
        const index = checkedList.indexOf(id);
        if (index > -1) {
          checkedList.splice(index, 1);
        }
        updateCheckBox(type, checkedList);
      } else {
        const checkedList = selectedCheckBox;
        checkedList.push(id);
        updateCheckBox(type, checkedList);
      }
    },

    getHardCheckinValues() {
      updateState({ isLoading: true });
      const hotelId = getSelectedHotel()?.key;
      let newHardCheckinStatus = 0;
      API.getHardCheckinValues({ hotelId })
        .then(response => {
          const { hardCheckin, paperlessHardCheckin, enabled } = response;
          if (hardCheckin === 1 && paperlessHardCheckin === 1) {
            newHardCheckinStatus = 2;
          }
          if (hardCheckin === 0 && paperlessHardCheckin === 1) {
            newHardCheckinStatus = 1;
          }
          if (hardCheckin === 0 && paperlessHardCheckin === 0) {
            newHardCheckinStatus = 0;
          }
          const isHardCheckinEnabled = enabled || 0;
          updateState({
            hardCheckinState: newHardCheckinStatus,
            isHardCheckinEnabledPMS: isHardCheckinEnabled,
          });
        })
        .catch(error => {
          updateState({ hardCheckinState: 0 });
        });
      updateState({ isLoading: false });
    },

    updateHardCheckin(applyToAllHotels) {
      if (!state.isHardCheckinEnabledPMS) return;
      const hotelId = applyToAllHotels ? null : state.selectedHotel?.key;
      const hardCheckinStatus = state.hardCheckinState;

      let hardCheckinValue = 0;
      let paperlessHardCheckinValue = 0;

      if (hardCheckinStatus === 2) {
        hardCheckinValue = 1;
        paperlessHardCheckinValue = 1;
      } else if (hardCheckinStatus === 1) {
        hardCheckinValue = 0;
        paperlessHardCheckinValue = 1;
      } else {
        hardCheckinValue = 0;
        paperlessHardCheckinValue = 0;
      }

      updateState({ isLoading: true });

      API.updateHardCheckin({ hotelId, hardCheckinValue, paperlessHardCheckinValue })
        .then(response => {})
        .catch(error => {});
    },
  };

  function updateState(object) {
    setState(previousState => ({ ...previousState, ...object }));
  }

  return { state, actions };
}

function getDisabledSelectors({ canDisable, key, value, disabledSelectors }) {
  if (canDisable.hasOwnProperty(key)) {
    const correspondingValue = canDisable[key];
    if (key === 'paperless_version' && value === 'v2') {
      disabledSelectors = disabledSelectors.filter(item => !correspondingValue.includes(item));
    } else if (!!value && key !== 'paperless_version') {
      disabledSelectors = disabledSelectors.filter(item => item !== correspondingValue);
    } else {
      if (Array.isArray(correspondingValue)) {
        correspondingValue.forEach(item => {
          if (!disabledSelectors.includes(item.value)) {
            disabledSelectors.push(item);
          }
        });
      } else {
        if (!disabledSelectors.includes(correspondingValue)) {
          disabledSelectors.push(correspondingValue);
        }
      }
    }
  }
  return disabledSelectors;
}
